import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, X, FileText, Image, Download, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import PDFViewer from '../ui/PDFViewer';

const ClientMessages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageContent, setMessageContent] = useState('');
  const [sending, setSending] = useState(false);
  const [adminId, setAdminId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    fetchAdminId();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const response = await api.get('/messages');
      setMessages(response.data);
      
      if (response.data.length > 0 && !adminId) {
        const adminMsg = response.data.find(m => m.sender_id !== user.id);
        if (adminMsg) {
          setAdminId(adminMsg.sender_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminId = async () => {
    try {
      // Try to get admin from existing messages, or fetch from API
      const response = await api.get('/messages');
      if (response.data.length > 0) {
        const adminMsg = response.data.find(m => m.sender_id !== user.id);
        if (adminMsg) {
          setAdminId(adminMsg.sender_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch admin ID:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limit to 5 attachments
    if (attachments.length + files.length > 5) {
      toast.error('Maximum 5 attachments allowed per message');
      return;
    }

    setUploading(true);
    const newAttachments = [];

    for (const file of files) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File "${file.name}" exceeds 10MB limit`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/messages/attachments', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newAttachments.push({
          id: response.data.id,
          filename: response.data.filename,
          file_size: response.data.file_size,
          mime_type: response.data.mime_type
        });
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        toast.error(`Failed to upload "${file.name}"`);
      }
    }

    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageContent.trim() && attachments.length === 0) {
      toast.error('Please enter a message or attach a file');
      return;
    }

    if (!adminId) {
      toast.error('No admin user found. Please wait for admin to message you first.');
      return;
    }

    setSending(true);
    try {
      await api.post('/messages', {
        recipient_id: adminId,
        content: messageContent,
        attachments: attachments.map(a => a.id)
      });
      toast.success('Message sent successfully!');
      setMessageContent('');
      setAttachments([]);
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handlePreviewAttachment = async (attachmentId, filename, mimeType) => {
    setPreviewAttachment({ id: attachmentId, filename, mime_type: mimeType });
    
    if (mimeType === 'application/pdf') {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setPreviewUrl(`${backendUrl}/api/messages/attachments/${attachmentId}/preview?token=${token}`);
    } else if (mimeType.startsWith('image/')) {
      try {
        const response = await api.get(`/messages/attachments/${attachmentId}/preview`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: mimeType });
        setPreviewUrl(URL.createObjectURL(blob));
      } catch (error) {
        console.error('Failed to load preview:', error);
        toast.error('Failed to load preview');
      }
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/messages/attachments/${attachmentId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error('Failed to download file');
    }
  };

  const closePreview = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewAttachment(null);
    setPreviewUrl(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const renderAttachments = (messageAttachments) => {
    if (!messageAttachments || messageAttachments.length === 0) return null;

    return (
      <div className="mt-2 space-y-1">
        {messageAttachments.map((attachmentId, idx) => (
          <div 
            key={idx}
            className="flex items-center space-x-2 bg-white/20 rounded px-2 py-1 text-xs"
          >
            <FileText className="w-3 h-3" />
            <span className="truncate flex-1">Attachment {idx + 1}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadAttachment(attachmentId, `attachment_${idx + 1}`);
              }}
              className="hover:opacity-70"
              title="Download"
            >
              <Download className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="messages-title">Secure Messages</h2>
        <p className="text-gray-600">Communicate with your PPMT team</p>
      </div>

      <div className="glass rounded-xl flex flex-col" style={{ height: '600px' }}>
        {/* Messages Area */}
        <div className="flex-1 p-6 overflow-y-auto" data-testid="messages-area">
          <div className="space-y-4">
            {messages.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No messages yet</p>
            ) : (
              messages.map((message, index) => (
                <div
                  key={message.id}
                  data-testid={`message-${index}`}
                  className={`flex ${message.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`message-bubble ${
                      message.sender_id === user.id ? 'sent' : 'received'
                    }`}
                  >
                    <p>{message.content}</p>
                    {renderAttachments(message.attachments)}
                    <p className={`text-xs mt-1 ${
                      message.sender_id === user.id ? 'text-white/70' : 'text-gray-500'
                    }`}>
                      {new Date(message.sent_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div className="px-6 py-2 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-2">
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 border shadow-sm"
                >
                  {getFileIcon(attachment.mime_type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{attachment.filename}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(attachment.file_size)}</p>
                  </div>
                  <button
                    onClick={() => removeAttachment(attachment.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Send Message Form */}
        <form onSubmit={handleSendMessage} className="p-6 border-t border-gray-200">
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <Textarea
                data-testid="message-input"
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Type your message to PPMT team..."
                className="w-full resize-none pr-12"
                rows={2}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute right-3 bottom-3 text-gray-400 hover:text-teal-600 transition-colors"
                title="Attach file"
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-600"></div>
                ) : (
                  <Paperclip className="w-5 h-5" />
                )}
              </button>
            </div>
            <Button
              data-testid="send-message-button"
              type="submit"
              disabled={sending || uploading}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            You can attach up to 5 files (max 10MB each). Supported: Images, PDF, Word, Excel, Text
          </p>
        </form>
      </div>

      {/* Attachment Preview Dialog */}
      <Dialog open={!!previewAttachment} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate pr-4">{previewAttachment?.filename}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDownloadAttachment(previewAttachment?.id, previewAttachment?.filename)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(90vh-100px)]">
            {previewAttachment?.mime_type?.startsWith('image/') && previewUrl ? (
              <img 
                src={previewUrl}
                alt={previewAttachment.filename}
                className="w-full h-auto"
              />
            ) : previewAttachment?.mime_type === 'application/pdf' && previewUrl ? (
              <div className="w-full h-[650px]">
                <PDFViewer 
                  url={previewUrl}
                  filename={previewAttachment.filename}
                  onDownload={() => handleDownloadAttachment(previewAttachment.id, previewAttachment.filename)}
                />
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                <Button onClick={() => handleDownloadAttachment(previewAttachment?.id, previewAttachment?.filename)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientMessages;
